// Starts the app: loads the start-up data from the server, provides the services the
// screen code calls (window.QV and window.QVStore), then loads the screen code (app.js).
import { api, postJson } from './api';
import { ServerStore } from './store';

type Qid = { on: boolean; only: boolean; session?: boolean; grants?: Record<string, number> };
type Boot = { demo: boolean; me: unknown | null; name?: string; pending?: string | null; store?: Record<string, unknown>; qid?: Qid };

declare global {
  interface Window { QV: QVApi; QVStore: ServerStore }
}

export type QVApi = {
  demo: boolean;
  signedIn: boolean;
  pending: string | null;
  name: string;
  /** Sign in with Qvilio ID: on/only, and for a Qvilio ID session the projects open through a support grant (until, ms). */
  qid: Qid | null;
  login(email: string, password: string, remember: boolean): Promise<any>;
  verify(code: string): Promise<any>;
  /** Ends the session; returns Qvilio ID's sign-out address for a Qvilio ID session. */
  logout(): Promise<string | null>;
  forgot(email: string): Promise<any>;
  checkToken(t: string): Promise<any>;
  setPassword(t: string, password: string): Promise<any>;
  demoAs(key: string): Promise<void>;
  audioPut(pid: string, id: string, blob: Blob): Promise<void>;
  audioGet(pid: string, id: string): Promise<Blob | null>;
  audioDel(pid: string, id: string): Promise<void>;
  userAction(action: string, email: string, pid: string): Promise<any>;
  flush(): Promise<void>;
  /** JSON request to the server API (Pitaj Qvilio, Usage & Cost, support, prices, metrics). */
  req(path: string, body?: unknown, method?: string): Promise<{ status: number; body: any }>;
};

async function start() {
  let boot: Boot;
  try {
    const r = await fetch('/api/boot', { credentials: 'same-origin', cache: 'no-store' });
    boot = await r.json();
  } catch {
    document.body.innerHTML = '<p style="font-family:system-ui;padding:40px">Server nije dostupan. Pokušajte ponovo za nekoliko trenutaka.</p>';
    return;
  }
  const store = new ServerStore(boot.store || {}, boot.me);
  window.QVStore = store;
  const audioUrl = (pid: string, id: string) => `/api/audio/${encodeURIComponent(pid)}/${encodeURIComponent(id)}`;
  window.QV = {
    demo: boot.demo,
    signedIn: !!boot.me,
    pending: boot.pending || null,
    name: boot.name || '',
    qid: boot.qid || null,
    login: (email, password, remember) => postJson('/api/auth/login', { email, password, remember }),
    verify: code => postJson('/api/auth/2fa', { code }),
    logout: async () => { await store.flush(); const r = await postJson('/api/auth/logout', {}); return typeof r?.redirect === 'string' ? r.redirect : null; },
    forgot: email => postJson('/api/auth/forgot', { email }),
    checkToken: t => fetch('/api/auth/token?t=' + encodeURIComponent(t)).then(r => r.json()),
    setPassword: (t, password) => postJson('/api/auth/password', { token: t, password }),
    demoAs: async key => { await store.flush(); await postJson('/api/auth/demo-as', { key }); },
    audioPut: async (pid, id, blob) => { await api(audioUrl(pid, id), { method: 'PUT', body: blob, headers: { 'Content-Type': blob.type || 'application/octet-stream' } }); },
    audioGet: async (pid, id) => { const r = await api(audioUrl(pid, id)); return r.ok ? r.blob() : null; },
    audioDel: async (pid, id) => { await api(audioUrl(pid, id), { method: 'DELETE' }); },
    userAction: async (action, email, pid) => { await store.flush(); return postJson('/api/auth/user-action', { action, email, pid }); },
    flush: () => store.flush(),
    req: async (path, body, method) => {
      await store.flush();
      let r: Response;
      try { r = await api('/api' + path, { method: method || (body === undefined ? 'GET' : 'POST'), ...(body === undefined ? {} : { body: JSON.stringify(body) }) }); }
      catch { return { status: 0, body: { error: 'Nema veze sa serverom. Proverite internet vezu i pokušajte ponovo.' } }; }
      let j: any = {};
      try { j = await r.json(); } catch { /* empty */ }
      return { status: r.status, body: j };
    },
  };
  // Provider calls (/proxy/...) carry the app header and the project being worked on.
  const nativeFetch = window.fetch.bind(window);
  // A header the browser cannot send (e.g. a masked value with "•") would make fetch throw
  // before any request leaves. Such headers are dropped; the value is never logged.
  const latin1 = (v: string) => !/[^\u0000-\u00ff]/.test(v);
  const cleanHeaders = (h: HeadersInit | undefined): HeadersInit | undefined => {
    if (!h || h instanceof Headers) return h;
    const pairs: [string, string][] = Array.isArray(h) ? h.map(([k, v]) => [k, String(v)]) : Object.entries(h).map(([k, v]) => [k, String(v)]);
    const ok = pairs.filter(([k, v]) => {
      const fine = latin1(k) && latin1(v);
      if (!fine) console.warn('[qv] header dropped (cannot be sent):', k);
      return fine;
    });
    return ok;
  };
  window.fetch = (input: RequestInfo | URL, init: RequestInit = {}) => {
    init = { ...init, headers: cleanHeaders(init.headers) };
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    if (url.startsWith('/proxy/')) {
      const headers = new Headers(init.headers);
      headers.set('X-QV', '1');
      const cur = (window as any).__qvCur?.();
      if (cur) headers.set('X-QV-Project', cur);
      return store.flush().then(() => nativeFetch(input, { ...init, headers, credentials: 'same-origin' }));
    }
    return nativeFetch(input, init);
  };
  addEventListener('pagehide', () => { void store.flush(true); });

  const s = document.createElement('script');
  s.src = '/app.js';
  document.body.appendChild(s);
}

void start();
